import React, { useContext } from 'react';
import GameContext from 'contexts/GameContext';
import { useSeatRotation } from 'contexts/SeatRotationContext';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { useTableSelector } from 'hooks/useTableSelector';
import { sortHoleCards } from 'utils/sortHoleCards';

import { SeatContainer } from 'components/Seat';
import { useWaitingSeatOccupants } from 'components/SitGoWaiting/useSitGoWaiting';

type SeatsPropertyType = {
  guid: any;
};

/**
 * Get the seat position for a specific player seat.
 * Maps seatId to the correct index in the positions array,
 * rotating based on the current player's seat.
 */
export const getSeatPosition = (
  seatPositions: SeatPosition[],
  players: any,
  seatId: number,
  currentPlayerSeatId: number
): SeatPosition | undefined => {
  const appSeats = players?.slice(1);
  const seatCount = appSeats.length;

  if (seatPositions.length === 0 || seatCount === 0) return undefined;

  let positionIndex = currentPlayerSeatId < 1 ? seatId - 1 : seatId - currentPlayerSeatId;
  if (positionIndex < 0) positionIndex = seatCount + positionIndex;

  return seatPositions[positionIndex % seatPositions.length];
};

/**
 * A seat's hole cards, ordered highest first.
 *
 * This USED to sort ascending for seats on the left of the felt and descending
 * for seats on the right, back when the cards fanned toward the seat's outer
 * side. EN-10520 made them fan LEFT on every seat, so the side-dependent half
 * was left over — and actively harmful, because `position.isLeft` is derived
 * from `leftPct < 40` and a TOP seat lands either side of that line depending on
 * orientation (portrait puts it at ~21%, landscape near ~50%).
 *
 * The effect was that rotating the device re-sorted a player's hand. That is
 * wrong on its own, and it also feeds SeatCards: the joined card list is the
 * dependency driving the deal / flip / sweep animation, so a reorder makes an
 * unchanged hand look like a new one to that state machine.
 *
 * Hand History shares this ordering through utils/sortHoleCards.
 */
const Seats = ({ guid }: SeatsPropertyType) => {
  const game = useTableSelector((table) => table.game);
  const players = useTableSelector((table) => table.players);
  const currentPlayerSeatId = useTableSelector((table) => table.currentPlayer.seatId);
  // EN-11257: a Sit & Go still filling draws its registrants in their seats,
  // and keeps them there through the start until the real seats arrive.
  const waitingOccupants = useWaitingSeatOccupants();

  const { seatPositions } = useContext(GameContext);
  const { isRotating, slotFor, seatAt, layoutSeatId: rotationSeatId } = useSeatRotation();
  // The seat the ring is laid out around — the rotation provider's, so the
  // settled layout and the spin agree: the viewer's seat, or (EN-11257) the
  // seat they were drawn while a Sit & Go starts, so nothing moves when the
  // game seats them there.
  const layoutSeatId = rotationSeatId ?? currentPlayerSeatId;

  const appSeats = players?.slice(1);

  return (
    <>
      {appSeats.map((seat: any, index: number) => {
        const seatId = index + 1;

        let positionIndex = layoutSeatId < 1 ? index : seatId - layoutSeatId;
        if (positionIndex < 0) positionIndex = appSeats.length + positionIndex;

        // While the POV seat-assignment rotation is in flight, ride the arc via
        // the continuous sampler; otherwise snap to the settled discrete slot.
        const position =
          (isRotating ? seatAt(slotFor(seatId)) : undefined) ??
          seatPositions[positionIndex % seatPositions.length];

        const cards: any = sortHoleCards([
          game[`p${seatId}card1`],
          game[`p${seatId}card2`],
          game[`p${seatId}card3`],
          game[`p${seatId}card4`],
          game[`p${seatId}card5`]
        ]);

        return (
          <SeatContainer
            index={index}
            position={position}
            currentSeatPositionLength={positionIndex}
            currentPlayerSeatId={currentPlayerSeatId}
            key={index}
            seat={seat as any}
            guid={guid}
            isFaceCardUp={seat.GUID === guid}
            seatId={seatId}
            cards={cards}
            seatPositionsLength={seatPositions.length}
            isRotating={isRotating}
            waitingRegistrant={waitingOccupants?.get(seatId)}
          />
        );
      })}
    </>
  );
};

export default Seats;
